import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { productsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { SpecsEditor } from './SpecsEditor';

const emptyProduct = {
    name: '',
    description: '',
    price: 0,
    original_price: 0,
    category: 'component',
    subcategory: '',
    condition: 'new',
    brand: '',
    specs: {},
    hidden_specs: {},
    images: [],
    stock: 0,
    stock_status: 'raktaron',
    featured: false,
    visible: true
};

export function ProductEditModal({ open, onOpenChange, product, onSaved }) {
    const [formData, setFormData] = useState(emptyProduct);
    const [imagesText, setImagesText] = useState('');
    const [saving, setSaving] = useState(false);
    const isEditing = !!product?.id;

    useEffect(() => {
        if (product) {
            setFormData({
                name: product.name || '',
                description: product.description || '',
                price: product.price || 0,
                original_price: product.original_price || 0,
                category: product.category || 'component',
                subcategory: product.subcategory || '',
                condition: product.condition || 'new',
                brand: product.brand || '',
                specs: product.specs || {},
                hidden_specs: product.hidden_specs || {},
                images: product.images || [],
                stock: product.stock || 0,
                stock_status: product.stock_status || (product.stock > 0 ? 'raktaron' : 'nincs_keszleten'),
                featured: product.featured || false,
                visible: product.visible !== false
            });
            setImagesText((product.images || []).join('\n'));
        } else {
            setFormData(emptyProduct);
            setImagesText('');
        }
    }, [product, open]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!formData.name || !formData.brand || !formData.price) {
            toast.error('Kérlek töltsd ki a kötelező mezőket (név, márka, ár)');
            return;
        }

        const imagesArr = imagesText.split('\n').filter(url => url.trim());

        const saveData = {
            ...formData,
            images: imagesArr,
            price: Number(formData.price),
            original_price: formData.original_price ? Number(formData.original_price) : null,
            stock: Number(formData.stock),
            stock_status: formData.stock_status || 'raktaron'
        };

        setSaving(true);

        try {
            if (isEditing) {
                await productsAPI.update(product.id, saveData);
                toast.success('Termék frissítve!');
            } else {
                await productsAPI.create(saveData);
                toast.success('Termék létrehozva!');
            }
            onOpenChange(false);
            if (onSaved) onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Hiba történt a mentéskor');
        }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Termék szerkesztése' : 'Új termék'}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                    {/* Basic info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Név *</Label>
                            <Input value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} />
                        </div>
                        <div>
                            <Label>Márka *</Label>
                            <Input value={formData.brand} onChange={(e) => handleInputChange('brand', e.target.value)} />
                        </div>
                    </div>
                    
                    <div>
                        <Label>Leírás</Label>
                        <Textarea 
                            value={formData.description} 
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            rows={3}
                        />
                    </div>
                    
                    {/* Category and condition */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <Label>Kategória</Label>
                            <Select value={formData.category} onValueChange={(v) => handleInputChange('category', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="gaming_pc">Gamer PC</SelectItem>
                                    <SelectItem value="office_pc">Irodai PC</SelectItem>
                                    <SelectItem value="workstation">Munkaállomás</SelectItem>
                                    <SelectItem value="component">Alkatrész</SelectItem>
                                    <SelectItem value="mobile_phone">Mobiltelefon</SelectItem>
                                    <SelectItem value="accessory">Tartozék</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Alkategória</Label>
                            <Select value={formData.subcategory || 'none'} onValueChange={(v) => handleInputChange('subcategory', v === 'none' ? '' : v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Nincs</SelectItem>
                                    <SelectItem value="cpu">Processzor</SelectItem>
                                    <SelectItem value="gpu">Videokártya</SelectItem>
                                    <SelectItem value="motherboard">Alaplap</SelectItem>
                                    <SelectItem value="ram">Memória</SelectItem>
                                    <SelectItem value="storage">Tárhely</SelectItem>
                                    <SelectItem value="psu">Tápegység</SelectItem>
                                    <SelectItem value="case">Ház</SelectItem>
                                    <SelectItem value="cooling">Hűtés</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Állapot</Label>
                            <Select value={formData.condition} onValueChange={(v) => handleInputChange('condition', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">Új</SelectItem>
                                    <SelectItem value="refurbished">Felújított</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    {/* Pricing */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Ár (Ft) *</Label>
                            <Input type="number" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} />
                        </div>
                        <div>
                            <Label>Eredeti ár (Ft)</Label>
                            <Input type="number" value={formData.original_price || ''} onChange={(e) => handleInputChange('original_price', e.target.value)} placeholder="Opcionális" />
                        </div>
                    </div>
                    
                    {/* Stock and flags */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <Label>Készlet (db)</Label>
                            <Input type="number" value={formData.stock} onChange={(e) => handleInputChange('stock', e.target.value)} />
                        </div>
                        <div>
                            <Label>Készlet státusz</Label>
                            <Select value={formData.stock_status || 'raktaron'} onValueChange={(v) => handleInputChange('stock_status', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="raktaron">Rakt&#225;ron</SelectItem>
                                    <SelectItem value="beszallitoi_raktaron">Besz&#225;ll&#237;t&#243;i rakt&#225;ron</SelectItem>
                                    <SelectItem value="rendelheto">Rendelhet&#337;</SelectItem>
                                    <SelectItem value="nincs_keszleten">Nincs k&#233;szleten</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-3 justify-center">
                            <div className="flex items-center gap-2">
                                <Checkbox id="edit-featured" checked={formData.featured} onCheckedChange={(v) => handleInputChange('featured', v)} />
                                <Label htmlFor="edit-featured">Kiemelt termék</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch 
                                    id="edit-visible" 
                                    checked={formData.visible !== false} 
                                    onCheckedChange={(v) => handleInputChange('visible', v)} 
                                />
                                <Label htmlFor="edit-visible" className="flex items-center gap-2">
                                    {formData.visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    {formData.visible !== false ? 'Látható' : 'Rejtett'}
                                </Label>
                            </div>
                        </div>
                    </div>
                    
                    {/* Specs */}
                    <SpecsEditor 
                        specs={formData.specs} 
                        onChange={(specs) => handleInputChange('specs', specs)}
                        label="Specifikációk (látható)"
                        category={formData.category}
                    />
                    
                    <SpecsEditor 
                        specs={formData.hidden_specs} 
                        onChange={(specs) => handleInputChange('hidden_specs', specs)}
                        label="Rejtett spec-ek (PC Builder)"
                    />
                    
                    {/* Images */}
                    <div>
                        <Label>Képek (URL-ek, soronként egy)</Label>
                        <Textarea 
                            value={imagesText} 
                            onChange={(e) => setImagesText(e.target.value)}
                            placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                            rows={3}
                        />
                    </div>
                </div>
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isEditing ? 'Mentés' : 'Létrehozás'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ProductEditModal;
